import {
  BrandedCampaignId,
  BtSid,
  CampaignPlacement,
  DateOfBirth,
  Decimal,
  Device,
  Gender,
  HealthcareSessionId,
  HealthcareUserId,
  Integer,
  Ircid,
  Irpid,
  ProductType,
  QualifyingLifeEvent,
  Source,
  SubId,
  SubsidyType,
  TobaccoUse,
  TrueFalse,
  USAStateAcronym,
  ZipCode,
} from '@hc-frontend/core-types';
import { alterDate } from '@hc-frontend/core-utils-date';
import { z } from 'zod';

export const UserAdsOptionsTracking = z.object({
  subid: SubId.optional(),
  btSid: BtSid.optional(),
  source: Source.optional(),
  irpid: Irpid.optional(),
  ircid: Ircid.optional(),
});

export const UserAdsOptionsHealthCare = z.object({
  sessionId: HealthcareSessionId,
  userId: HealthcareUserId,
});

// TODO: This will be required once the session is created in the middleware
export const UserAdsOptionsLocation = z.object({
  stateAcronym: USAStateAcronym.optional().default(USAStateAcronym.Enum.FL),
  zipCode: ZipCode.optional().default('33177'),
});

export const UserAdsOptionsQualificationHouseHold = z.object({
  income: Decimal.optional(),
  size: Integer.optional(),
});

export const UserAdsOptionsQualification = z.object({
  household: UserAdsOptionsQualificationHouseHold,
  qualifyLifeEvent: QualifyingLifeEvent.optional(),
  brandedCampaignId: BrandedCampaignId.optional(),
  subsidyType: SubsidyType.default(
    SubsidyType.Enum.NoSubsidyEligible,
  ).optional(),
});

export const UserAdsOptionsCampaign = z.object({
  utmSource: Source.optional(),
});

export const UserAdsOptionsUser = z.object({
  device: Device.optional(),
});

export const UserAdsOptionsApplicant = z.object({
  gender: Gender.optional(),
  tobacco: TobaccoUse.optional(),
  dob: DateOfBirth.optional(),
  childrenCount: Integer,
  married: TrueFalse,
});

export const UserAdsOptionsConfig = z.object({
  productType: ProductType,
  placement: CampaignPlacement,
  limit: z.number().optional(),
});

export const UserAdsOptions = z.object({
  tracking: UserAdsOptionsTracking,
  healthcare: UserAdsOptionsHealthCare,
  location: UserAdsOptionsLocation,
  qualification: UserAdsOptionsQualification,
  campaign: UserAdsOptionsCampaign,
  user: UserAdsOptionsUser,
  applicant: UserAdsOptionsApplicant,
  config: UserAdsOptionsConfig,
});

export const DefaultUserAdsOptions = UserAdsOptions.extend({
  applicant: UserAdsOptionsApplicant.extend({
    dob: DateOfBirth.default(new Date()),
    childrenCount: Integer.default(0),
    married: TrueFalse.default(false),
    gender: Gender.default(Gender.Enum.MALE),
  }),
  tracking: UserAdsOptionsTracking.extend({
    source: UserAdsOptionsTracking.shape.source.unwrap().default('ORGANIC'),
  }),
  user: UserAdsOptionsUser.extend({
    device: UserAdsOptionsUser.shape.device
      .unwrap()
      .default(Device.Enum.DESKTOP),
  }),
  qualification: UserAdsOptionsQualification.extend({
    household: UserAdsOptionsQualificationHouseHold.extend({
      income: UserAdsOptionsQualificationHouseHold.shape.income.default(0),
      size: UserAdsOptionsQualificationHouseHold.shape.size.default(1),
    }),
    qualifyLifeEvent: UserAdsOptionsQualification.shape.qualifyLifeEvent
      .unwrap()
      .default(QualifyingLifeEvent.Enum.Coverage),
  }),
}).transform((value) => {
  const newValue = value;

  if (!newValue.applicant.dob)
    if (newValue.config.productType === ProductType.Enum.HEALTH) {
      newValue.applicant.dob = alterDate({
        years: -35,
      });
    } else {
      newValue.applicant.dob = alterDate({
        years: -65,
      });
    }

  // TODO: compute the household size based on the number of children when initializing the session
  if (!newValue.qualification.household.size) {
    newValue.qualification.household.size =
      1 +
      newValue.applicant.childrenCount +
      (newValue.applicant.married ? 1 : 0);
  }

  if (!newValue.qualification.household.income) {
    newValue.qualification.household.income = 0;
  }

  if (!newValue.applicant.tobacco) {
    newValue.applicant.tobacco = TobaccoUse.Enum.NO;
  }

  if (!newValue.applicant.gender) {
    newValue.applicant.gender = Gender.Enum.MALE;
  }

  return newValue;
});

export type UserAdsOptions = z.infer<typeof UserAdsOptions>;

export type DefaultUserAdsOptions = z.infer<typeof DefaultUserAdsOptions>;
